import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Card, CardContent, Typography, Stack, Button, IconButton, Chip,
  Avatar, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, Alert, Divider, LinearProgress, TextField, InputAdornment,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { LoadingButton } from "@mui/lab";
import Autocomplete from "@mui/material/Autocomplete";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import axios from "axios";

import { SupervisorUid } from "../components/constants";

const normalizeBaseUrl = (raw) => {
  if (!raw) return "";
  const trimmed = raw.trim().replace(/\/$/, "");
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const firstOnly = (full = "") => full.split(" ").filter(Boolean)[0] || full;

export default function NotificationRolesPage() {
  const base = useMemo(
    () => normalizeBaseUrl(localStorage.getItem("saved_sett_api_url") || ""),
    [],
  );

  const [employees, setEmployees] = useState([]);

  const [roles, setRoles] = useState([]);
  const [actions, setActions] = useState([]);

  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [snack, setSnack] = useState({ open: false, message: "", severity: "info" });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create");
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedSupervisors, setSelectedSupervisors] = useState([]);

  const [selectedActions, setSelectedActions] = useState([]);
  const [otherName, setOtherName] = useState("");
  const [editingCode, setEditingCode] = useState(null);
  const [formError, setFormError] = useState("");

  const [saving, setSaving] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);

  const [deletingRole, setDeletingRole] = useState(null); 

  const [deleting, setDeleting] = useState(false);



  const supervisorUidSet = useMemo(                                   // add
     () => new Set(SupervisorUid.map((u) => String(u).trim())),        // add
     [],                                                               //  add
   );                                                                  //  add
   const assignableEmployees = useMemo(                                //  add
     () => employees.filter((e) => !supervisorUidSet.has(String(e.empid))), //  add
     [employees, supervisorUidSet],                                    //  add
   ); 
   
   

  const empById = useMemo(() => {
    const map = {};
    employees.forEach((e) => {
      map[e.empid] = e;
    });
    return map;
  }, [employees]);

  const empName = (id) => empById[id]?.name || String(id);

  const actionName = (id) => actions.find((a) => Number(a.actionId) === Number(id))?.actionName || String(id);


  const empSearchLabel = (e) => {
    if (!e) return "";
    return e.code ? `${e.code} - ${e.name}` : e.name;
  };

  const filterByNameOrCode = (options, state) => {
    const q = String(state.inputValue ?? "").trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      String(o.name).toLowerCase().includes(q) ||
      String(o.code).toLowerCase().includes(q) ||
      String(o.empid).includes(q),
    );
  };

  const fetchAll = async () => {
    if (!base) {
      setLoading(false);
      setSnack({ open: true, message: "API URL not set in Settings.", severity: "error" });
      return;
    }
    setLoading(true);
    try {
      const [supRes, empRes, actRes] = await Promise.all([
        axios.get(`${base}/api/notify/roles`),
        axios.get(`${base}/api/employees`),
        axios.get(`${base}/api/notify/actions`),
      ]);
      const empPayload = empRes?.data;
      const empList = Array.isArray(empPayload)
        ? empPayload
        : Array.isArray(empPayload?.data)
          ? empPayload.data
          : [];
      setEmployees(
        empList
          .map((x) => ({
            empid: Number(x.empid ?? x.EmpId ?? x.empId ?? 0),
            code: String(x.code ?? x.EmpCode ?? ""),
            name: String(x.name ?? x.fullName ?? "").trim(),
          }))
          .filter((e) => e.empid && e.name),
      );
      setRoles(Array.isArray(supRes.data) ? supRes.data : []);
      setActions(Array.isArray(actRes.data) ? actRes.data : []);
    } catch {
      setSnack({ open: true, message: "Failed to load notification roles.", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);


  //! if i want to remove the case where the  person is employee and supervisor at the same time 
  //! and put this pers as employee for other supervisors and for the other supervisor make it as employee in other cases ! 
//   const groupedRows = useMemo(() => {
//     const byKey = {};
//     Object.entries(supervisorsByEmp).forEach(([empId, supSet]) => {
//       const supervisorIds = [...supSet].sort((a, b) => a - b);
//       const key = supervisorIds.join(",");
//       if (!byKey[key]) byKey[key] = { id: key, supervisorIds, empIds: [] };
//       byKey[key].empIds.push(Number(empId));
//     });
//     return Object.values(byKey);
//   }, [supervisorsByEmp]);

   const groupedRows = useMemo(
    () => roles.map((r) => ({
      id: r.otherCode,
      otherCode: r.otherCode,
      otherName: r.otherName,
      empIds: r.empIds || [],
      supervisorIds: r.supervisorIds || [],
      actionIds: r.actionIds || [],
    })),
    [roles],
  );


  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groupedRows;
    return groupedRows.filter(
      (r) =>
        (r.otherName || "").toLowerCase().includes(q) ||
        r.empIds.some((id) => empName(id).toLowerCase().includes(q)) ||
        r.supervisorIds.some((id) => empName(id).toLowerCase().includes(q)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedRows, search, empById]);

 

    const openCreate = () => {
    setDialogMode("create");
    setOtherName("");
    setEditingCode(null);
    setFormError("");
    setSelectedEmployees([]);
    setSelectedSupervisors([]);
    setSelectedActions([]);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setDialogMode("edit");
    setOtherName(row.otherName || "");
    setEditingCode(row.otherCode);
    setFormError("");
    setSelectedEmployees(row.empIds.map((id) => empById[id]).filter(Boolean));
    setSelectedSupervisors(row.supervisorIds.map((id) => empById[id]).filter(Boolean));
    setSelectedActions(actions.filter((a) => row.actionIds.includes(Number(a.actionId))));
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setOtherName("");
    setEditingCode(null);
    setFormError("");
    setSelectedEmployees([]);
    setSelectedSupervisors([]);
    setSelectedActions([]);
  };


  // Sync from local to cloud 
  const syncNotifySupervisors = async () => {
    try {
      await axios.get(`${base}/api/notify-supervisor/sync`, { withCredentials: true });
    } catch {
      setSnack({ open: true, message: "Saved locally, but sync to cloud failed.", severity: "warning" });
    }
  };

     const saveAssignment = async () => {
    if (!otherName.trim()) {
      setFormError("Notification role name is required.");
      return;
    }
    if (!selectedEmployees.length) {
      setFormError("Select at least one employee.");
      return;
    }
    if (!selectedSupervisors.length) {
      setFormError("Select at least one supervisor.");
      return;
    }
    if (!selectedActions.length) {
      setFormError("Select at least one action.");
      return;
    }

    setFormError("");
    setSaving(true);
    try {
      await axios.post(`${base}/api/notify/roles`, {
        otherCode: editingCode,
        otherName: otherName.trim(),
        empIds: selectedEmployees.map((e) => e.empid),
        supervisorIds: selectedSupervisors.map((s) => s.empid),
        actionIds: selectedActions.map((a) => a.actionId),
      });
      setSnack({
        open: true,
        message: dialogMode === "create" ? "Notification role created." : "Notification role updated.",
        severity: "success",
      });
      closeDialog();
      await fetchAll();
      await syncNotifySupervisors();
    } catch (e) {
      const conflicts = e.response?.data?.conflicts;
      if (Array.isArray(conflicts) && conflicts.length) {
        const text = conflicts.map((c) => `${empName(c.empId)} (${c.otherName})`).join(", ");
        setFormError(`Already in another group role: ${text}. Remove them from that role first.`);
      } else {
        setFormError(e.response?.data?.error || "Save failed. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const askDelete = (row) => {
    setDeletingRole(row);
    setConfirmOpen(true);
  };


  const confirmDelete = async () => {
    if (!deletingRole) {
      setConfirmOpen(false);
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(`${base}/api/notify/roles/${deletingRole.otherCode}`);
      setSnack({ open: true, message: "Notification role deleted.", severity: "success" });
      await fetchAll();
      await syncNotifySupervisors();
    } catch {
      setSnack({ open: true, message: "Delete failed.", severity: "error" });
    } finally {
      setDeleting(false);
      setDeletingRole(null);
      setConfirmOpen(false);
    }
  };

  const renderPeople = (ids, variant) => {
    const names = ids.map(empName);
    if (!names.length) {
        return <Typography variant="body2" color="text.secondary">-</Typography>;

    }
    return (
       <Tooltip
        arrow
        title={
          <React.Fragment>
            {names.map((n, i) => (
              <div key={i}>{n}</div>
            ))}
          </React.Fragment>
        }
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            label={firstOnly(names[0])}
            size="small"
            color={variant === "supervisor" ? "primary" : "default"}
            variant={variant === "supervisor" ? "outlined" : "filled"}
          />
          {names.length > 1 && (
            <Chip label={`+${names.length - 1} more`} size="small" variant="outlined" />
          )}
        </Stack>
      </Tooltip>
    );
  };

    const columns = [
    {
      field: "otherName",
      headerName: "Notification role",
      headerAlign: "center",
      flex: 1.1,
      minWidth: 180,
      renderCell: (params) => (
        <Chip
          label={params.row.otherName}
          sx={{ bgcolor: "#00897B20", color: "#00897B", fontWeight: 700, borderRadius: 1.5 }}
        />
      ),
    },
    {
      field: "empIds",
      headerName: "Employees",
      headerAlign: "center",
      flex: 1.3,
      minWidth: 260,
      sortable: false,
      renderCell: (params) => renderPeople(params.row.empIds, "employee"),
    },
    {
      field: "supervisorIds",
      headerName: "Notified supervisors",
      headerAlign: "center",
      flex: 1.3,
      minWidth: 260,
      sortable: false,
      renderCell: (params) => renderPeople(params.row.supervisorIds, "supervisor"),
    },
    {
      field: "actionIds",
      headerName: "Notify on",
      headerAlign: "center",
      flex: 1.2,
      minWidth: 220,
      sortable: false,
      renderCell: (params) => {
        const names = params.row.actionIds.map(actionName);
        if (!names.length) {
          return <Typography variant="body2" color="text.secondary">-</Typography>;
        }
        return (
          <Tooltip arrow title={<React.Fragment>{names.map((n, i) => <div key={i}>{n}</div>)}</React.Fragment>}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip label={names[0]} size="small" color="secondary" variant="outlined" />
              {names.length > 1 && <Chip label={`+${names.length - 1} more`} size="small" variant="outlined" />}
            </Stack>
          </Tooltip>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",
      align: "center",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => openEdit(params.row)}>
              <EditRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => askDelete(params.row)}>
              <DeleteRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  const NoRows = () => (
    <Stack height="100%" alignItems="center" justifyContent="center" spacing={1.5}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#004d40" }}>
        No notification supervisors yet
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Choose employees and the supervisors who should hear about their attendance actions.
      </Typography>
      <Button onClick={openCreate} variant="contained" startIcon={<AddRoundedIcon />} sx={{ borderRadius: 2 }}>
        Add notification supervisors
      </Button>
    </Stack>
  );

  const pickerProps = {
    options: employees,
    getOptionLabel: empSearchLabel,
    filterOptions: filterByNameOrCode,
    isOptionEqualToValue: (opt, val) => Number(opt.empid) === Number(val.empid),
    renderOption: (props, option) => (
      <li {...props} key={option.empid}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip size="small" label={option.code || option.empid} sx={{ borderRadius: 2 }} />
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {option.name}
          </Typography>
        </Stack>
      </li>
    ),
  };

  const selectAllAdornment = (params, onSelectAll, title) => (
    <>
      <Tooltip title={title}>
        <IconButton
          size="small"
          onClick={onSelectAll}
          sx={{ mr: 0.5, bgcolor: "rgba(0,150,136,0.06)", "&:hover": { bgcolor: "rgba(0,150,136,0.12)" } }}
        >
          <SelectAllIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {params.InputProps.endAdornment}
    </>
  );

  return(
     <Box
      sx={{
        minHeight: "100vh",
        p: { xs: 2, md: 3 },
        display: "flex",
        justifyContent: "center",
        background: `radial-gradient(1200px 600px at -10% -20%, rgba(0,121,107,0.18), transparent 60%),
                     radial-gradient(900px 500px at 120% 120%, rgba(0,151,167,0.16), transparent 60%),
                     linear-gradient(135deg, #e0f2f1 0%, #ffffff 60%)`,
      }}
    >

      <Card
        elevation={0}
        sx={{
          width: 1100,
          maxWidth: "98vw",
          borderRadius: 4,
          overflow: "hidden",
          backdropFilter: "blur(10px)",
          bgcolor: "rgba(255,255,255,0.96)",
          boxShadow: "0 10px 30px rgba(0,0,0,.08), 0 20px 60px rgba(0,121,107,.15)",
          border: "1px solid",
          borderColor: "#b2dfdb",
        }}
      >
        {loading && <LinearProgress color="success" sx={{ borderRadius: "4px 4px 0 0" }} />}

        <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "start", sm: "center" }}
            justifyContent="space-between"
            gap={2}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: "#00838f", width: 56, height: 56, boxShadow: "0 8px 24px rgba(0,131,143,.30)" }}>
                <NotificationsActiveRoundedIcon fontSize="medium" />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#004d40" }}>
                  Other Roles 
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Every supervisor listed for an employee gets an email and WhatsApp for each of that employee's attendance actions.
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <TextField
                size="small"
                placeholder="Search employee or supervisor"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 240 }}
              />
              <Button
                variant="contained"
                onClick={openCreate}
                startIcon={<AddRoundedIcon />}
                sx={{
                  borderRadius: 2,
                  bgcolor: "#004d40",
                  ":hover": { bgcolor: "#003d33" },
                  boxShadow: "0 10px 24px rgba(0,77,64,.25)",
                  whiteSpace: "nowrap",
                }}
              >
                New assignment
              </Button>
            </Stack>
          </Stack>

          <Divider sx={{ my: 2.5 }} />

          <Box sx={{ height: 520, width: "100%" }}>
            <DataGrid
              rows={filteredRows}
              getRowId={(r) => r.id}
              columns={columns}
              disableRowSelectionOnClick
              pageSizeOptions={[5, 10, 25]}
              initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
              slots={{ noRowsOverlay: NoRows }}
              sx={{
                "& .MuiDataGrid-columnHeaders": { bgcolor: "rgba(0,150,136,0.06)" },
                "& .MuiDataGrid-cell": { display: "flex", alignItems: "center", justifyContent: "center" },
                "& .MuiDataGrid-columnHeaderTitle": { width: "100%", textAlign: "center" },
                "& .MuiDataGrid-row:hover": { background: "rgba(0,150,136,0.04)" },
                borderColor: "#b2dfdb",
                borderRadius: 2,
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}
      >
        <Box sx={{ height: 6, background: "linear-gradient(90deg, #00897B 0%, #0097A7 100%)" }} />
       {formError && (
          <Alert severity="error" sx={{ m: 2, mb: 0, borderRadius: 2 }}>
            {formError}
          </Alert>
        )}

        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 900, color: "#0f172a" }}>
            {dialogMode === "create" ? "New notification role" : "Edit notification role"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            All selected supervisors are notified at the same time. There is no order and no escalation.
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2, pb: 2.5 }}>
                    <Stack spacing={2.25} sx={{ mt: 1 }}>

            <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid rgba(0,0,0,0.08)", bgcolor: "rgba(2,132,199,0.02)" }}>
              <CardContent sx={{ p: 2.25 }}>
                <Stack spacing={1.75}>
                  <Stack spacing={0.25}>
                    <Typography sx={{ fontWeight: 900, color: "#0f172a" }}>Basics</Typography>
                    <Typography variant="body2" color="text.secondary">
                      A name that tells HR which group this is.
                    </Typography>
                  </Stack>
                  <TextField
                    label="Notification role name"
                    required
                    value={otherName}
                    onChange={(e) => setOtherName(e.target.value)}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.25 } }}
                  />
                </Stack>
              </CardContent>
            </Card>

            <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid rgba(0,0,0,0.08)", bgcolor: "#ffffff" }}>
              <CardContent sx={{ p: 2.25 }}>
                <Stack spacing={1.75}>
                  <Stack spacing={0.25}>
                    <Typography sx={{ fontWeight: 900, color: "#0f172a" }}>Select the employee(s)</Typography>
                    <Typography variant="body2" color="text.secondary">
                      An employee can only be in one notification role at a time.
                    </Typography>
                  </Stack>
                  <Autocomplete
                    multiple
                    {...pickerProps}
                    options={assignableEmployees}
                    value={selectedEmployees}
                    onChange={(_, v) => setSelectedEmployees(v)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Employees"
                        placeholder="Type name or code"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: selectAllAdornment(params, () => setSelectedEmployees(assignableEmployees), "Select all employees"),
                        }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.25 } }}
                      />
                    )}
                  />
                </Stack>
              </CardContent>
            </Card>

            <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid rgba(0,0,0,0.08)", bgcolor: "#ffffff" }}>
              <CardContent sx={{ p: 2.25 }}>
                <Stack spacing={1.75}>
                  <Stack spacing={0.25}>
                    <Typography sx={{ fontWeight: 900, color: "#0f172a" }}>Select the supervisors to notify</Typography>
                    <Typography variant="body2" color="text.secondary">
                      All of them are notified at the same time. There is no order and no escalation.
                    </Typography>
                  </Stack>
                  <Autocomplete
                    multiple
                    {...pickerProps}
                    value={selectedSupervisors}
                    onChange={(_, v) => setSelectedSupervisors(v)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Supervisors to notify"
                        placeholder="Type name or code"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: selectAllAdornment(params, () => setSelectedSupervisors(employees), "Select all as supervisors"),
                        }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.25 } }}
                      />
                    )}
                  />
                </Stack>
              </CardContent>
            </Card>

            <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid rgba(0,0,0,0.08)", bgcolor: "#ffffff" }}>
              <CardContent sx={{ p: 2.25 }}>
                <Stack spacing={1.75}>
                  <Stack spacing={0.25}>
                    <Typography sx={{ fontWeight: 900, color: "#0f172a" }}>Select the actions</Typography>
                    <Typography variant="body2" color="text.secondary">
                      The supervisors get an email and WhatsApp only when the employee does one of these actions. For leave and visit requests, the employee's approvers already get the approval message, so they are not notified twice.
                    </Typography>
                  </Stack>
                  <Autocomplete
                    multiple
                    options={actions}
                    value={selectedActions}
                    getOptionLabel={(a) => a?.actionName || ""}
                    isOptionEqualToValue={(a, b) => Number(a.actionId) === Number(b.actionId)}
                    onChange={(_, v) => setSelectedActions(v)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Notify on these actions"
                        placeholder="Choose actions"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: selectAllAdornment(params, () => setSelectedActions(actions), "Select all actions"),
                        }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.25 } }}
                      />
                    )}
                  />
                </Stack>
              </CardContent>
            </Card>

          </Stack>
         </DialogContent>






        <DialogActions
          sx={{
            p: 2.25,
            bgcolor: "rgba(0,0,0,0.02)",
            borderTop: "1px solid rgba(0,0,0,0.06)",
            justifyContent: "space-between",
          }}
        >
          <Button onClick={closeDialog} color="inherit" sx={{ fontWeight: 800 }}>
            Cancel
          </Button>
          <LoadingButton
            loading={saving}
            onClick={saveAssignment}
            variant="contained"
            sx={{ borderRadius: 2.25, px: 2.25, bgcolor: "#0f766e", ":hover": { bgcolor: "#0b5f59" }, fontWeight: 900 }}
          >
            {dialogMode === "create" ? "Save assignment" : "Save changes"}
          </LoadingButton>
        </DialogActions>
      </Dialog>

    <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle>Delete notification role?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {`"${deletingRole?.otherName || ""}" will be deleted, and its ${deletingRole?.empIds?.length || 0} employee(s) will stop sending notifications until you add them to another role.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <LoadingButton color="error" variant="contained" loading={deleting} onClick={confirmDelete}>
            Remove
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

